import { useEffect, useState } from 'react'
import { api, type StandortZeile } from '../api'
import { anzahl, euro, prozent } from '../format'
import { Anteile } from '../komponenten/Anteile'
import { Karte } from '../komponenten/Karte'

/**
 * Alle Standorte nebeneinander.
 *
 * Bei zwölf Standorten liegt der Wert weniger in der einzelnen Zahl als
 * im Vergleich: Welcher hebt sich ab, und wobei? Deshalb steht neben
 * jeder Quote ein Balken gegen den höchsten Wert der Spalte — die Zahl
 * bleibt ablesbar, das Verhältnis sieht man auf einen Blick.
 */
export function Standortvergleich({ von, bis }: { von: string; bis: string }) {
  const [zeilen, setZeilen] = useState<StandortZeile[]>([])
  const [fehler, setFehler] = useState<string | null>(null)
  const [laedt, setLaedt] = useState(true)

  useEffect(() => {
    let weg = false
    setLaedt(true); setFehler(null)
    api.betriebe(von, bis)
      .then((d) => { if (!weg) setZeilen(d.betriebe) })
      .catch((e) => { if (!weg) setFehler(e.message) })
      .finally(() => { if (!weg) setLaedt(false) })
    return () => { weg = true }
  }, [von, bis])

  if (fehler) return <div className="fehler">{fehler}</div>
  if (laedt && !zeilen.length) return <div className="laedt">Wird geladen …</div>
  if (!zeilen.length) {
    return <div className="karte"><div className="leer">
      Keine Standorte mit Daten im Zeitraum.
    </div></div>
  }

  const hoechst = (f: (z: StandortZeile) => number | null) =>
    Math.max(...zeilen.map((z) => f(z) ?? 0), 0.0001)
  const maxStorno = hoechst((z) => z.stornoquote)
  const maxSofort = hoechst((z) => z.sofort_je_100)
  const gesamt = zeilen.reduce((s, z) => s + Number(z.umsatz), 0)

  const balken = (v: number | null, max: number) => (
    <div className="zeilenbalken-spur" style={{ width: 54, display: 'inline-block',
                                                marginLeft: 8, verticalAlign: 'middle' }}>
      <div className="zeilenbalken"
           style={{ width: `${Math.max(2, ((v ?? 0) / max) * 100)}%` }} />
    </div>
  )

  return (
    <>
      <Karte titel="Anteil am Gesamtumsatz" neben={euro(gesamt)}>
        <Anteile
          anteile={zeilen.map((z) => ({ name: z.betrieb, wert: Number(z.umsatz) }))}
          formatiere={euro} />
      </Karte>

      <Karte titel="Die Standorte im Vergleich"
             neben={`${zeilen.length} Standorte`}
             flach>
        <div style={{ overflowX: 'auto' }}>
          <table>
            <thead>
              <tr>
                <th>Standort</th>
                <th className="r">Umsatz</th>
                <th className="r">Anteil</th>
                <th className="r">Tage</th>
                <th className="r">je Tag</th>
                <th className="r">Rechnungen</th>
                <th className="r">Ø Rechnung</th>
                <th className="r">Storno %</th>
                <th className="r">Rabatt %</th>
                <th className="r">Trinkgeld %</th>
                <th className="r">Bar %</th>
                <th className="r">Sofortst. je 100</th>
              </tr>
            </thead>
            <tbody>
              {zeilen.map((z) => (
                <tr key={z.id}>
                  <td>{z.betrieb}</td>
                  <td className="r zahl">{euro(z.umsatz)}</td>
                  <td className="r zahl dezent">{prozent(z.anteil, 1)}</td>
                  <td className="r zahl dezent">{anzahl(z.tage)}</td>
                  <td className="r zahl">{euro(z.umsatz_je_tag)}</td>
                  <td className="r zahl dezent">{anzahl(z.rechnungen)}</td>
                  <td className="r zahl">{euro(z.durchschnittsbon)}</td>
                  <td className="r zahl" style={{ whiteSpace: 'nowrap' }}>
                    {prozent(z.stornoquote, 2)}{balken(z.stornoquote, maxStorno)}
                  </td>
                  <td className="r zahl">{prozent(z.rabattquote, 2)}</td>
                  <td className="r zahl">{prozent(z.trinkgeldquote, 2)}</td>
                  <td className="r zahl">{prozent(z.barquote, 1)}</td>
                  <td className="r zahl" style={{ whiteSpace: 'nowrap' }}>
                    {z.sofort_je_100 === null ? '–'
                      : new Intl.NumberFormat('de-DE',
                          { maximumFractionDigits: 2 }).format(z.sofort_je_100)}
                    {balken(z.sofort_je_100, maxSofort)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p style={{ margin: '14px 0 0', fontSize: 11, color: 'var(--schrift-3)',
                    lineHeight: 1.6 }}>
          Quoten sind hier aus den Summen des ganzen Zeitraums gebildet, nicht
          aus den Tagesquoten gemittelt. Der Unterschied ist nicht kosmetisch:
          beim Mitteln zählte ein schwacher Montag so viel wie ein starker
          Samstag.
        </p>
      </Karte>
    </>
  )
}
